import React, { FC, useCallback, useState, useEffect, useRef } from 'react';
import { Grid, Typography, FormControl, Button, Alert } from '@mui/material';
import { ErrorAlert } from '@/components/forms/alerts/ErrorAlert/ErrorAlert';
import { CenteredContentWrapper } from '@/components/layout/CenteredContentWrapper/CenteredContentWrapper';
import { Link } from '@/components/general/Link/Link';
import { useAuthStore } from '@/store/authStore/authStore';
import { Method2fa } from '@/types/enums/auth/Method2fa';
import { useRouter, usePhrase, useRedirectUrl } from '@/hooks';
import { use2faMethods } from '@/hooks/queries/auth';
import { useForm } from 'react-hook-form';
import { LOGIN_PHRASES } from '@/consts/phrases/loginPhrases';
import { getRedirectURLFromStatus } from '@/utils/auth/getRedirectURLFromStatus';
import { SendCodeButton } from '@/components/general/buttons/SendCodeButton/SendCodeButton';
import { FetchErrorsType, ICountryData } from '@/types';
import { TwoFactorSubmitFormSkeleton } from './TwoFactorSubmitFormSkeleton';
import { isArrayWithValues } from '@/utils/fp';
import { LOGIN_ERROR_PHRASES } from '@/consts/phrases/errorPhrases';
import { useExtractedFetchErrorMessage } from '@/hooks/general/useExtractedFetchErrorMessage';
import { useConfigStore } from '@/store/configStore/configStore';
import { OTPField } from '@/components/forms/inputs/OTPField/OTPField';

import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

import styles from 'styles/modules/Auth.module.css';

interface TwoFactorSubmitProps {
    isDisabled?: boolean;
    countriesDict: ICountryData[] | undefined;
}

interface TwoFactorSubmitFormValues {
    code: string;
}

const initialData: { code: string } = {
    code: '',
};

export const TwoFactorSubmitForm: FC<TwoFactorSubmitProps> = ({
    isDisabled = false,
    countriesDict,
}) => {
    const methodsInitialised = useRef(false);
    const router = useRouter();
    const { t } = usePhrase();
    const { data: methods, isLoading: isLoadingMethods, error: methodsError } = use2faMethods();
    const methodsErrorMessage = useExtractedFetchErrorMessage(methodsError);

    const redirectUrl = useRedirectUrl();

    const [validationError, setValidationError] = useState<FetchErrorsType>();

    const hasMoreThanOneMethod = methods && methods.length > 1;
    const isLoading = isLoadingMethods || !methodsInitialised;
    const hasAttempted2faCodeSend = useRef(false);

    const { website_2fa_email_resend_code_timeout } = useConfigStore((state) => ({
        website_2fa_email_resend_code_timeout: state.website_2fa_email_resend_code_timeout,
    }));

    const {
        codeIds,
        method,
        secondsUntilNext2faCode,
        sendMethod2fa,
        validateCode,
        loggedInEmail,
        code2faPhoneNumber,
        code2faSentViaMethodPage,
        resetCode2faSentViaMethodPage,
    } = useAuthStore((state) => ({
        codeIds: state.code2fa.codeIds,
        method: state.code2fa.method,
        secondsUntilNext2faCode: state.code2fa.secondsUntilNext2faCode,
        code2faPhoneNumber: state.code2fa.phoneNumber,
        code2faSentViaMethodPage: state.code2fa.sentViaMethodPage,
        resetCode2faSentViaMethodPage: state.resetCode2faSentViaMethodPage,
        sendMethod2fa: state.sendMethod2fa,
        validateCode: state.validateCode2fa,
        loggedInEmail: state.loggedInEmail,
    }));

    const hasNoMethod =
        (!method && methodsInitialised.current && !isLoadingMethods) ||
        (!isArrayWithValues(methods) && !isLoadingMethods);

    const missingEmailOrPhoneNumber =
        (method === Method2fa.Email && !loggedInEmail) ||
        (method === Method2fa.Sms && !code2faPhoneNumber);

    const { control, handleSubmit, formState, setError } = useForm<TwoFactorSubmitFormValues>({
        defaultValues: initialData,
    });
    const { isSubmitting, errors } = formState;

    const onSubmit = async (data: { code: string }) => {
        if (!method) return;
        const res = await validateCode({
            method,
            code: data.code,
            codeId: codeIds[method],
            countriesDict,
        });
        if (res.type === 'error') {
            const { generalErrors, propertyErrors } = res.error || {
                generalErrors: [],
                propertyErrors: {},
            };
            if (generalErrors && generalErrors?.[0]?.phraseId) {
                setValidationError(res.error);
            }
            if (propertyErrors && propertyErrors.code?.[0]?.phraseId) {
                setError('code', {
                    type: 'manual',
                    message: t(propertyErrors.code[0].phraseId, { asString: true }),
                });
            }
        } else if (typeof res.message === 'string') {
            if (res.messagePhraseId) {
                const queryParams = {
                    redirectUrl: getRedirectURLFromStatus(
                        res.message,
                        res.destinationUrl ?? redirectUrl,
                    ),
                    messagePhraseId: res.messagePhraseId.toString(),
                };

                router.push(`/login/message?${new URLSearchParams(queryParams).toString()}`);
            } else {
                if (!res.hasUidRedirect) {
                    router.push(getRedirectURLFromStatus(res.message, redirectUrl));
                }
            }
        }
    };

    const send2faCode = useCallback(
        async (currentMethod?: Method2fa) => {
            const res = await sendMethod2fa(
                currentMethod || method,
                website_2fa_email_resend_code_timeout,
            );
            if (res.type === 'error') {
                setValidationError(res.error);
                return false;
            } else {
                setValidationError(undefined);
                return true;
            }
        },
        [method, sendMethod2fa, website_2fa_email_resend_code_timeout],
    );

    const onResendCodeClicked = async (): Promise<boolean> => {
        const hasCodeSent = await send2faCode();
        return hasCodeSent;
    };

    useEffect(() => {
        if (!methodsInitialised.current && methods) {
            const initialiseMethods = async () => {
                const hasOnlyOneMethod = methods.length === 1;
                const currentMethod = hasOnlyOneMethod ? methods[0] : (method ?? methods[0]);
                const currentMethodShouldBeRequested = currentMethod !== Method2fa.Totp; // TOTP method requires no code request

                const hasRequestCodeCondition =
                    !code2faSentViaMethodPage &&
                    currentMethod &&
                    currentMethodShouldBeRequested &&
                    !hasAttempted2faCodeSend.current;

                if (hasRequestCodeCondition) {
                    hasAttempted2faCodeSend.current = true;
                    await send2faCode(currentMethod);
                } else {
                    resetCode2faSentViaMethodPage();
                }
                methodsInitialised.current = true;
            };
            void initialiseMethods();
        }
    }, [
        methods,
        send2faCode,
        methodsError,
        method,
        secondsUntilNext2faCode,
        code2faSentViaMethodPage,
        resetCode2faSentViaMethodPage,
    ]);

    const getMethodMessage = () => {
        let detail: string | React.ReactNode = '';
        if (method === Method2fa.Email) {
            if (loggedInEmail) {
                detail = loggedInEmail;
            }
        } else if (method === Method2fa.Sms) {
            if (code2faPhoneNumber) {
                detail = code2faPhoneNumber;
            }
        } else if (method === Method2fa.Totp) {
            return (
                <Typography variant="body1" sx={{ mb: 2 }}>
                    {t(LOGIN_PHRASES.USE_OTC_FROM_APP)}
                </Typography>
            );
        }

        return (
            <>
                <Typography variant="body1">
                    {t(LOGIN_PHRASES.SENT_OTC)}{' '}
                    <b style={{ textTransform: 'lowercase' }}>{detail}</b>
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                    {t(LOGIN_PHRASES.ENTER_CODE_BELOW)}
                </Typography>
            </>
        );
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.auth}>
            <CenteredContentWrapper
                navigationContent={
                    hasMoreThanOneMethod ? (
                        <Link href="/login/2fa/method">
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<KeyboardBackspaceIcon />}
                            >
                                {t(LOGIN_PHRASES.BACK)}
                            </Button>
                        </Link>
                    ) : null
                }
            >
                {isLoading ? (
                    <TwoFactorSubmitFormSkeleton />
                ) : (
                    <>
                        <Typography variant="h2" align="center" className={styles.title}>
                            {t(LOGIN_PHRASES.ENTER_OTC)}
                        </Typography>
                        {methodsError && (
                            <ErrorAlert
                                id="methods_error_message_box"
                                errorMessage={methodsErrorMessage}
                                sx={{ mb: 2 }}
                            />
                        )}
                        {hasNoMethod || missingEmailOrPhoneNumber ? (
                            <Alert severity="error" id="general_error_message_box">
                                {t(LOGIN_ERROR_PHRASES.SOMETHING_WRONG_RETURN_TO_LOGIN)}
                            </Alert>
                        ) : (
                            <Grid container spacing={1}>
                                <Grid size={12} sx={{ mb: 2 }}>
                                    {getMethodMessage()}
                                </Grid>
                                {validationError && (
                                    <Grid
                                        size={12}
                                        className={styles.validationErrorBlock}
                                        sx={{ mb: 2 }}
                                    >
                                        <ErrorAlert
                                            id="validation_error_message_box"
                                            errorMessage={validationError}
                                        />
                                    </Grid>
                                )}
                                <>
                                    <Grid size={12}>
                                        <OTPField
                                            name="code"
                                            id="code_field"
                                            length={method === Method2fa.Totp ? 6 : 5}
                                            control={control}
                                            onComplete={(code) => onSubmit({ code })}
                                            disabled={isDisabled || isSubmitting}
                                            error={errors.code}
                                            required
                                        />
                                    </Grid>
                                    {method !== Method2fa.Totp && (
                                        <Grid size={12} sx={{ mt: 0.5 }}>
                                            <FormControl fullWidth>
                                                <SendCodeButton
                                                    onClick={onResendCodeClicked}
                                                    refreshInterval={secondsUntilNext2faCode}
                                                />
                                            </FormControl>
                                        </Grid>
                                    )}
                                </>
                            </Grid>
                        )}
                    </>
                )}
            </CenteredContentWrapper>
        </form>
    );
};
